/**
 * Asks which file format an export should produce, for screens whose Export
 * action (`SectionHeader export`) can write either a JSON file (the one Import
 * reads back) or a CSV file (for a spreadsheet). The Export slot is a single
 * action on desktop and an overflow entry on a phone, so the choice is a
 * dialog rather than a menu anchored to the button.
 */

import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Radio, RadioGroup,
} from '@mui/material';

export type ExportFormat = 'json' | 'csv';

export interface ExportFormatDialogProps {
  open: boolean;
  onClose: () => void;
  /** Runs the export in the chosen format; the dialog closes first. */
  onExport: (format: ExportFormat) => void;
}

export const ExportFormatDialog: React.FC<ExportFormatDialogProps> = ({ open, onClose, onExport }) => {
  const translate = useTranslate();
  const [format, setFormat] = useState<ExportFormat>('json');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('shell.exportDialog.title')}</DialogTitle>
      <DialogContent>
        <RadioGroup
          value={format}
          onChange={(e) => setFormat(e.target.value as ExportFormat)}
          aria-label={translate('shell.exportDialog.title')}
        >
          <FormControlLabel value="json" control={<Radio />} label={translate('shell.exportDialog.json')} />
          <FormControlLabel value="csv" control={<Radio />} label={translate('shell.exportDialog.csv')} />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onClose();
            onExport(format);
          }}
        >
          {translate('ra.action.export')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportFormatDialog;
